import { useMemo } from 'react';

import type { AspectRatio } from '@uncinc/react-drupal-core/dist/Components/DynamicMediaItem';

import useWindowDimensions from './useWindowDimensions';

import type { LayoutVariables } from '../types';
import {
  LAYOUT_VARIABLES,
  SCREEN_LG,
  SCREEN_MD,
  SCREEN_SM,
  SCREEN_XL,
} from '../config/constants';

type RestLayoutVariables<K extends keyof LayoutVariables> = Omit<LayoutVariables[K],
| 'heroImageRatioXS'
| 'heroImageRatioSM'
| 'heroImageRatioMD'
| 'heroImageRatioLG'
| 'heroImageRatioXL'
>;

const heroImageAspectRatios = [
  'heroImageRatioXS',
  'heroImageRatioSM',
  'heroImageRatioMD',
  'heroImageRatioLG',
  'heroImageRatioXL',
];

export type AspectRatiosList = [AspectRatio, AspectRatio, AspectRatio, AspectRatio, AspectRatio];

const useLayoutVariables = <K extends keyof LayoutVariables>(key: K) => {
  const { width } = useWindowDimensions();

  const layoutVariables = LAYOUT_VARIABLES[key];

  const restLayoutVariables = Object.fromEntries(
    Object.entries(layoutVariables).filter(([variableKey]) => !heroImageAspectRatios.includes(variableKey)),
  ) as RestLayoutVariables<K>;

  const heroImageAspectRatio = useMemo(() => {
    if (
      'heroImageRatioXS' in layoutVariables &&
      width < SCREEN_SM
    ) {
      return layoutVariables.heroImageRatioXS;
    }

    if (
      'heroImageRatioSM' in layoutVariables &&
      width >= SCREEN_SM &&
      width < SCREEN_MD
    ) {
      return layoutVariables.heroImageRatioSM;
    }

    if (
      'heroImageRatioMD' in layoutVariables &&
      width >= SCREEN_MD &&
      width < SCREEN_LG
    ) {
      return layoutVariables.heroImageRatioMD;
    }

    if (
      'heroImageRatioLG' in layoutVariables &&
      width >= SCREEN_LG &&
      width < SCREEN_XL
    ) {
      return layoutVariables.heroImageRatioLG;
    }

    if (
      'heroImageRatioXL' in layoutVariables &&
      width >= SCREEN_XL
    ) {
      return layoutVariables.heroImageRatioXL;
    }

    return 'original';
  }, [layoutVariables, width]);

  const heroImageBreakpointAspectRatios: AspectRatiosList = useMemo(() => ([
    'heroImageRatioXS' in layoutVariables ? layoutVariables.heroImageRatioXS : 'original',
    'heroImageRatioSM' in layoutVariables ? layoutVariables.heroImageRatioSM : 'original',
    'heroImageRatioMD' in layoutVariables ? layoutVariables.heroImageRatioMD : 'original',
    'heroImageRatioLG' in layoutVariables ? layoutVariables.heroImageRatioLG : 'original',
    'heroImageRatioXL' in layoutVariables ? layoutVariables.heroImageRatioXL : 'original',
  ]), [layoutVariables]);

  return {
    heroImageAspectRatio,
    heroImageBreakpointAspectRatios,
    ...restLayoutVariables,
  };
};

export default useLayoutVariables;
